import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const featuredCaseStudies = [
  {
    id: "1",
    slug: "it-services-expansion",
    title: "IT Services Company Expansion",
    industry: "IT & Technology",
    location: "Mumbai",
    workstations: 350,
    timeline: "6 weeks",
    image: "/case-studies/case-workstations.jpg",
    excerpt: "Delivered 350 workstations with 40% space optimization, completed 2 weeks ahead of schedule.",
  },
  {
    id: "2",
    slug: "banking-hq-renovation",
    title: "Banking HQ Renovation",
    industry: "BFSI",
    location: "Delhi NCR",
    workstations: 120,
    timeline: "8 weeks",
    image: "/case-studies/case-directors-cabin.jpg",
    excerpt: "Premium executive floor renovation that became a benchmark for the bank's other branches.",
  },
  {
    id: "3",
    slug: "coworking-space-setup",
    title: "Co-working Space Setup",
    industry: "Co-working",
    location: "Bangalore",
    workstations: 450,
    timeline: "10 weeks",
    image: "/case-studies/case-partitions.jpg",
    excerpt: "450-seat flexible workspace that achieved 95% occupancy within 3 months of launch.",
  },
];

export default function FeaturedCaseStudies() {
  return (
    <section className="py-24 bg-[#0F172A] noise-overlay" data-testid="case-studies-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
            Case Studies
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white max-w-2xl">
              Projects That Define Excellence
            </h2>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-white font-semibold hover:text-[#F97316] transition-colors"
            >
              View All Projects
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredCaseStudies.map((study, index) => (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group"
            >
              <Link
                to={`/case-studies/${study.slug}`}
                className="block"
                data-testid={`case-study-${study.slug}`}
              >
                <div className="relative overflow-hidden mb-6 card-image-zoom">
                  <div className="aspect-[4/3]">
                    <picture>
                      <source srcSet={study.image.replace(/\.(png|jpe?g)$/i, ".webp")} type="image/webp" />
                      <img src={study.image}
                        alt={study.title}
                        className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </picture>
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#F97316] text-white text-xs font-mono px-3 py-1 uppercase tracking-wider">
                      {study.industry}
                    </span>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#F97316] transition-colors">
                  {study.title}
                </h3>
                <p className="text-[#94A3B8] text-sm mb-4 line-clamp-2">
                  {study.excerpt}
                </p>

                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2 text-[#64748B]">
                    <MapPin className="w-4 h-4" />
                    {study.location}
                  </div>
                  <div className="flex items-center gap-2 text-[#64748B]">
                    <Users className="w-4 h-4" />
                    {study.workstations} seats
                  </div>
                  <div className="flex items-center gap-2 text-[#64748B]">
                    <Clock className="w-4 h-4" />
                    {study.timeline}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <Button
            asChild
            className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0F172A] rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
          >
            <Link to="/case-studies">
              Explore All Case Studies
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
